<template>
  <div class="inductor-storage-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$W_L = ½ L I² &nbsp;|&nbsp; ε = -L (dI/dt)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          
          <!-- Circuit wires -->
          <line x1="100" y1="180" x2="300" y2="180" stroke="#475569" stroke-width="2" />
          <line x1="300" y1="180" x2="300" y2="80" stroke="#475569" stroke-width="2" />
          <line x1="100" y1="180" x2="100" y2="80" stroke="#475569" stroke-width="2" />

          <!-- Power Source -->
          <g transform="translate(190, 170)">
             <rect x="-10" y="0" width="20" height="20" fill="#facc15" stroke="#ca8a04" stroke-width="2" rx="2" />
             <text x="0" y="14" font-size="10" font-weight="900" text-anchor="middle" fill="#854d0e">DC</text>
             <text x="-25" y="15" font-size="12" fill="#cbd5e1" font-weight="bold">+</text>
             <text x="25" y="13" font-size="14" fill="#cbd5e1" font-weight="bold">-</text>
          </g>

          <!-- Switch -->
          <g transform="translate(100, 80)">
             <line x1="0" y1="0" :x2="switchClosed ? 30 : 25" :y2="switchClosed ? 0 : -20" stroke="#cbd5e1" stroke-width="3" stroke-linecap="round" />
             <circle cx="0" cy="0" r="4" fill="#94a3b8" />
             <circle cx="30" cy="0" r="4" fill="#94a3b8" />
             
             <!-- Spark Arching -->
             <g v-if="sparking">
                <path d="M 25,-20 L 28,-10 L 26,-5 L 30,0" fill="none" stroke="#22d3ee" stroke-width="2" filter="url(#glow)">
                   <animate attributeName="opacity" values="1;0;1" dur="0.05s" repeatCount="indefinite" />
                </path>
                <circle cx="28" cy="-10" r="6" fill="rgba(34, 211, 238, 0.4)" filter="url(#glow)" />
             </g>
          </g>
          
          <line x1="130" y1="80" x2="150" y2="80" stroke="#475569" stroke-width="2" />

          <!-- Inductor component -->
          <g transform="translate(150, 80)">
             <!-- Coil loops -->
             <path v-for="i in turns" :key="'back'+i" :d="`M ${i*coilSpacing},0 Q ${i*coilSpacing + coilSpacing/2},25 ${(i+1)*coilSpacing},0`" fill="none" stroke="#64748b" stroke-width="2" stroke-dasharray="2,2" />
             
             <!-- Magnetic Core (Optional visualizing) -->
             <rect x="0" y="-8" :width="(turns+1)*coilSpacing" height="16" fill="rgba(148, 163, 184, 0.1)" />

             <path v-for="i in turns" :key="'front'+i" :d="`M ${i*coilSpacing},0 Q ${i*coilSpacing + coilSpacing/2},-25 ${(i+1)*coilSpacing},0`" fill="none" stroke="#fbbf24" stroke-width="3" stroke-linecap="round" />

             <!-- Changing Magnetic Field B -->
             <g v-if="currentI > 0.1">
                <line v-for="j in 3" :key="'bf'+j" :x1="-20" :y1="-10 + j*5" :x2="(turns+1)*coilSpacing + 20" :y2="-10 + j*5" stroke="#38bdf8" stroke-width="1.5" stroke-dasharray="8,4" :opacity="Math.min(1, currentI/10)">
                   <animate attributeName="stroke-dashoffset" from="12" to="0" dur="0.5s" repeatCount="indefinite" />
                </line>
             </g>
          </g>

          <line :x1="150 + turns*coilSpacing + coilSpacing" y1="80" x2="300" y2="80" stroke="#475569" stroke-width="2" />

          <!-- Current Flow Indicator -->
          <g v-if="currentI > 0.1">
             <circle cx="100" cy="130" r="4" fill="#a3e635">
                <animate attributeName="cy" from="180" to="80" :dur="`${2/currentI}s`" repeatCount="indefinite" />
             </circle>
             <circle cx="300" cy="130" r="4" fill="#a3e635">
                <animate attributeName="cy" from="80" to="180" :dur="`${2/currentI}s`" repeatCount="indefinite" />
             </circle>
          </g>

          <defs>
             <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                   <feMergeNode in="coloredBlur"/>
                   <feMergeNode in="SourceGraphic"/>
                </feMerge>
             </filter>
          </defs>

        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>电路瞬态电流 I</label>
              <span class="val">{{ currentI.toFixed(2) }} <small>A</small></span>
           </div>
           <div class="hud-item orange">
              <label>电感磁场储能 W_L</label>
              <span class="val">{{ storedEnergy.toFixed(2) }} <small>J</small></span>
           </div>
           <div class="hud-item red" v-if="sparking || emfSpike > 0">
              <label>自感电动势 ε (断电千伏击穿)</label>
              <span class="val" style="color:#ef4444;">{{ Math.abs(emfSpike).toFixed(0) }} <small>V</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节电感量 L (Henry)</label>
                    <el-slider v-model="inductanceL" :min="1" :max="20" :step="1" size="small"></el-slider>
                 </div>
                 <div class="flex-1">
                    <el-button :type="switchClosed ? 'danger' : 'primary'" size="small" @click="toggleSwitch" class="w-full" :icon="switchClosed ? 'el-icon-lock' : 'el-icon-unlock'">
                       {{ switchClosed ? '极其突然地断开开关' : '闭合开关 (开始通电储能)' }}
                    </el-button>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>电感器是著名的“电磁惯性”元件。闭合开关后，电流不会立刻达到最大值，因为电流上升会导致磁通量增加，电感会产生<strong>反向电动势</strong>阻碍电流增加，逐渐将电能转化为磁场能 <code>W = ½LI²</code> 储存起来。而当极其猛烈地<strong>断开开关</strong>时，电流 <code>I</code> 试图瞬间掉到0，导致极大的电流变化率 <code>dI/dt → ∞</code>，此时自感电动势 <code>ε = -L(dI/dt)</code> 会瞬间飙升到数千伏特，直接击穿开关处的空气，产生极为耀眼的电火花，强制释放磁场能！</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InductorStorageViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      inductanceL: 5, // Henry
      voltage: 12,    // DC Voltage
      resistance: 2,  // Circuit resistance Ohms
      currentI: 0,
      switchClosed: false,
      sparking: false,
      emfSpike: 0,
      turns: 10,
      coilSpacing: 10,
      timer: null
    }
  },
  computed: {
    targetCurrent() {
       return this.switchClosed ? (this.voltage / this.resistance) : 0;
    },
    storedEnergy() {
       return 0.5 * this.inductanceL * this.currentI * this.currentI;
    }
  },
  methods: {
    toggleSwitch() {
       if (this.switchClosed) {
          // Opening the switch suddenly
          this.switchClosed = false;
          // Calculate dI/dt loosely over a 10ms air breakdown timeframe
          let dIdt = -this.currentI / 0.01; 
          this.emfSpike = -this.inductanceL * dIdt;
          
          if (this.currentI > 1.0) {
             this.sparking = true;
          }
          
          // Energy dissipated in the spark rapidly
          setTimeout(() => {
             this.sparking = false;
             this.emfSpike = 0;
          }, 300);

       } else {
          // Closing the switch
          this.switchClosed = true;
       }
    },
    animate() {
       if (this.switchClosed) {
          // L(dI/dt) + RI = V  => dI/dt = (V - RI)/L
          let dIdt = (this.voltage - this.resistance * this.currentI) / this.inductanceL;
          this.currentI += dIdt * 0.1; // 100ms per frame integration
       } else {
          if (this.sparking) {
             // Draining through spark resistance (extremely high)
             this.currentI += (-this.currentI / 0.1) * 0.1; 
             if (this.currentI < 0) this.currentI = 0;
          } else {
             this.currentI = 0;
          }
       }
       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    if(this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.inductor-storage-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } &.red { border-left: 3px solid #ef4444; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; } .w-full { width: 100%; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
